import axios from 'axios'
import { apiClient } from '@/shared/services/client'
import { API_BASE_URL } from '@/shared/lib/constants'
import type {
  PaginatedEncuestas,
  EncuestaDetail,
  EncuestaCreatePayload,
  EncuestaUpdatePayload,
  Campo,
  CampoCreatePayload,
  CampoUpdatePayload,
  SuccessResponse,
  ReporteResumen,
  PaginatedSesiones,
  EstadisticasEncuesta,
  EncuestaPublica,
  SubmitPayload,
  SubmitResult,
  GraciasData,
  ResultadosData,
  DatosPacientePayload,
} from '../types'

const BASE = '/encuestas'

// Cliente público sin JWT
const publicClient = axios.create({
  baseURL: `${API_BASE_URL}/encuestas/publico`,
})

// === ADMIN - ENCUESTAS ===

export async function getEncuestas(params?: {
  page?: number
  page_size?: number
  search?: string
  grupo_laboratorio_id?: number
}): Promise<PaginatedEncuestas> {
  const { data } = await apiClient.get(`${BASE}/admin/`, { params })
  return data
}

export async function getEncuesta(id: number): Promise<EncuestaDetail> {
  const { data } = await apiClient.get(`${BASE}/admin/${id}/`)
  return data
}

export async function crearEncuesta(payload: EncuestaCreatePayload): Promise<EncuestaDetail> {
  const { data } = await apiClient.post(`${BASE}/admin/`, payload)
  return data
}

export async function actualizarEncuesta(id: number, payload: EncuestaUpdatePayload): Promise<EncuestaDetail> {
  const { data } = await apiClient.put(`${BASE}/admin/${id}/`, payload)
  return data
}

export async function eliminarEncuesta(id: number): Promise<SuccessResponse> {
  const { data } = await apiClient.delete(`${BASE}/admin/${id}/`)
  return data
}

export async function toggleEncuesta(id: number): Promise<SuccessResponse> {
  const { data } = await apiClient.post(`${BASE}/admin/${id}/toggle/`)
  return data
}

// === ADMIN - CAMPOS ===

export async function crearCampo(encuestaId: number, payload: CampoCreatePayload): Promise<Campo> {
  const { data } = await apiClient.post(`${BASE}/admin/${encuestaId}/campos/`, payload)
  return data
}

export async function actualizarCampo(encuestaId: number, campoId: number, payload: CampoUpdatePayload): Promise<Campo> {
  const { data } = await apiClient.put(`${BASE}/admin/${encuestaId}/campos/${campoId}/`, payload)
  return data
}

export async function eliminarCampo(encuestaId: number, campoId: number): Promise<SuccessResponse> {
  const { data } = await apiClient.delete(`${BASE}/admin/${encuestaId}/campos/${campoId}/`)
  return data
}

export async function reordenarCampos(encuestaId: number, campo_ids: number[]): Promise<SuccessResponse> {
  const { data } = await apiClient.post(`${BASE}/admin/${encuestaId}/campos/reordenar/`, campo_ids)
  return data
}

// === REPORTES ===

export async function exportarExcel(encuestaId: number): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/reportes/${encuestaId}/exportar/`, {
    responseType: 'blob',
  })
  return data
}

export async function descargarQR(encuestaId: number): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/reportes/${encuestaId}/qr/`, {
    responseType: 'blob',
  })
  return data
}

export async function getDashboardReportes(params?: { grupo_laboratorio_id?: number }): Promise<ReporteResumen> {
  const { data } = await apiClient.get(`${BASE}/reportes/`, { params })
  return data
}

export async function getReporteDatos(encuestaId: number, params?: {
  page?: number
  page_size?: number
}): Promise<PaginatedSesiones> {
  const { data } = await apiClient.get(`${BASE}/reportes/${encuestaId}/datos/`, { params })
  return data
}

export async function getReporteEstadisticas(encuestaId: number): Promise<EstadisticasEncuesta> {
  const { data } = await apiClient.get(`${BASE}/reportes/${encuestaId}/estadisticas/`)
  return data
}

export async function reiniciarEncuesta(encuestaId: number): Promise<SuccessResponse> {
  const { data } = await apiClient.post(`${BASE}/reportes/${encuestaId}/reiniciar/`)
  return data
}

export async function eliminarRespuesta(encuestaId: number, sesionId: number): Promise<SuccessResponse> {
  const { data } = await apiClient.delete(`${BASE}/reportes/${encuestaId}/respuesta/${sesionId}/`)
  return data
}

// === PÚBLICO (sin auth) ===

export async function getEncuestaPublica(uuid: string): Promise<EncuestaPublica> {
  const { data } = await publicClient.get(`/${uuid}/`)
  return data
}

export async function submitEncuesta(uuid: string, payload: SubmitPayload): Promise<SubmitResult> {
  const { data } = await publicClient.post(`/${uuid}/submit/`, payload)
  return data
}

export async function getGraciasData(uuid: string): Promise<GraciasData> {
  const { data } = await publicClient.get(`/${uuid}/gracias/`)
  return data
}

export async function getResultadosData(uuid: string, sesionUuid: string): Promise<ResultadosData> {
  const { data } = await publicClient.get(`/${uuid}/resultados/${sesionUuid}/`)
  return data
}

export async function guardarDatosPaciente(payload: DatosPacientePayload): Promise<{ message: string; success: boolean }> {
  const { data } = await publicClient.post('/datos-paciente/', payload)
  return data
}
